// src/components/features/content/ContentCalendar.jsx
import {
    Add as AddIcon,
    CheckCircle as ApproveIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    ViewDay as DayViewIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    ViewModule as MonthViewIcon,
    MoreVert as MoreVertIcon,
    Send as PublishIcon,
    Cancel as RejectIcon,
    Schedule as ScheduleIcon,
    Today as TodayIcon,
    ViewWeek as WeekViewIcon
} from '@mui/icons-material';
import {
    alpha,
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    Menu,
    MenuItem,
    Paper,
    Select,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import {
    addDays,
    addMonths,
    addWeeks,
    endOfMonth,
    endOfWeek,
    format,
    startOfMonth,
    startOfWeek,
    subDays,
    subMonths,
    subWeeks
} from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Import custom components
import { MagicLoader } from '../../../design-system';
import CalendarGrid from './CalendarGrid';

// Import hooks
import useAuthStore from '../../../stores/authStore';
import useContentStore from '../../../stores/contentStore';
import useNotificationStore from '../../../stores/notificationStore';
import PlatformChip from '../../ui/PlatformChip';
import ContentTypeIcon from '../../ui/ContentTypeIcon';
import { EntityStatusBadge } from '../../core';

// Calendar Event Component
const CalendarEvent = ({ event, onClick, onActionClick }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  
  const handleActionOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  
  const handleActionClose = () => {
    setAnchorEl(null);
  };
  
  const handleAction = (action) => {
    handleActionClose();
    onActionClick(action, event);
  };
  
  // Determine card color based on content status
  const getStatusColor = () => {
    switch (event.status) {
      case 'published':
        return alpha(theme.palette.success.main, 0.1);
      case 'scheduled':
        return alpha(theme.palette.info.main, 0.1);
      case 'pendingApproval':
        return alpha(theme.palette.warning.main, 0.1);
      case 'rejected':
        return alpha(theme.palette.error.main, 0.1);
      default:
        return alpha(theme.palette.grey[500], 0.1);
    }
  };
  
  return (
    <Box 
      sx={{
        mb: 1,
        bgcolor: getStatusColor(),
        borderLeft: `3px solid ${theme.palette[event.status === 'published' 
          ? 'success' 
          : event.status === 'scheduled' 
            ? 'info' 
            : event.status === 'pendingApproval' 
              ? 'warning' 
              : event.status === 'rejected' 
                ? 'error' 
                : 'grey'
        ].main}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        borderRadius: 1,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 2,
        },
      }}
      onClick={onClick}
    >
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ContentTypeIcon type={event.type} />
            <Typography variant="body2" noWrap sx={{ ml: 0.5, maxWidth: 120 }}>
              {event.title}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="caption" color="textSecondary" sx={{ mr: 0.5 }}>
              {format(new Date(event.scheduledTime), 'HH:mm')}
            </Typography>
            <IconButton size="small" onClick={handleActionOpen}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
          <PlatformChip platform={event.platform} size="small" />
          <EntityStatusBadge status={event.status} entityType="content" size="small" sx={{ ml: 0.5 }} />
        </Box>
      </Box>
      
      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={() => handleAction('edit')}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        
        {event.status !== 'published' && (
          <MenuItem onClick={() => handleAction('schedule')}>
            <ScheduleIcon fontSize="small" sx={{ mr: 1 }} /> 
            {event.status === 'scheduled' ? 'Reschedule' : 'Schedule'}
          </MenuItem>
        )}
        
        {event.status === 'scheduled' && (
          <MenuItem onClick={() => handleAction('publish')}>
            <PublishIcon fontSize="small" sx={{ mr: 1 }} /> Publish Now
          </MenuItem>
        )}
        
        {event.status === 'pendingApproval' && (
          <>
            <MenuItem onClick={() => handleAction('approve')}>
              <ApproveIcon fontSize="small" sx={{ mr: 1, color: theme.palette.success.main }} /> Approve
            </MenuItem>
            <MenuItem onClick={() => handleAction('reject')}>
              <RejectIcon fontSize="small" sx={{ mr: 1, color: theme.palette.error.main }} /> Reject
            </MenuItem>
          </>
        )}
        
        <MenuItem onClick={() => handleAction('delete')} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

// Content Calendar Component
const ContentCalendar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Store hooks
  const { 
    contentCalendar,
    isLoading,
    selectedDate,
    fetchContentCalendar,
    setSelectedDate
  } = useContentStore();
  
  const { showSuccess } = useNotificationStore();
  const { hasPermission } = useAuthStore();
  
  // Local state
  const [events, setEvents] = useState({});
  const [viewMode, setViewMode] = useState('month');
  const [brandFilter, setBrandFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Check permissions
  const canCreate = hasPermission('content', 'create');
  
  // Fetch calendar data on component mount and when date changes
  useEffect(() => {
    loadCalendarData();
  }, [selectedDate, viewMode]);
  
  // Process content calendar data into events
  useEffect(() => {
    const formattedEvents = {};
    
    try {
      // Check if contentCalendar is an object and not null
      if (contentCalendar && typeof contentCalendar === 'object') {
        // Process the data
        Object.keys(contentCalendar).forEach(date => {
          if (Array.isArray(contentCalendar[date])) {
            formattedEvents[date] = contentCalendar[date].map(item => ({
              ...item,
              scheduledTime: item.scheduledTime ? new Date(item.scheduledTime) : new Date()
            }));
          }
        });
      }
    } catch (err) {
      console.log('Error formatting calendar events:', err);
    }
    
    setEvents(formattedEvents);
    setIsInitialLoad(false);
  }, [contentCalendar]);
  
  // Load calendar data
  const loadCalendarData = async () => {
    // Calculate date range based on view
    let start, end;
    
    if (viewMode === 'month') {
      start = startOfMonth(selectedDate);
      end = endOfMonth(selectedDate);
    } else if (viewMode === 'week') {
      start = startOfWeek(selectedDate);
      end = endOfWeek(selectedDate);
    } else if (viewMode === 'day') {
      start = selectedDate;
      end = selectedDate;
    }
    
    await fetchContentCalendar({
      dateRange: { start, end },
      brandId: brandFilter !== 'all' ? brandFilter : undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
    });
  };
  
  // Handle date navigation
  const handlePrevious = () => {
    if (viewMode === 'month') {
      setSelectedDate(subMonths(selectedDate, 1));
    } else if (viewMode === 'week') {
      setSelectedDate(subWeeks(selectedDate, 1));
    } else if (viewMode === 'day') {
      setSelectedDate(subDays(selectedDate, 1));
    }
  };
  
  const handleNext = () => {
    if (viewMode === 'month') {
      setSelectedDate(addMonths(selectedDate, 1));
    } else if (viewMode === 'week') {
      setSelectedDate(addWeeks(selectedDate, 1));
    } else if (viewMode === 'day') {
      setSelectedDate(addDays(selectedDate, 1));
    }
  };
  
  const handleToday = () => {
    setSelectedDate(new Date());
  };
  
  // Handle view mode change
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };
  
  // Handle day click
  const handleDayClick = (day) => {
    if (viewMode === 'month') {
      setSelectedDate(day);
      setViewMode('day');
    }
  };
  
  // Handle event click
  const handleEventClick = (event) => {
    navigate(`/content/${event.contentId}`);
  };
  
  // Handle event actions
  const handleEventAction = (action, event) => {
    switch (action) {
      case 'edit':
        navigate(`/content/${event.contentId}/edit`);
        break;
      case 'schedule':
        navigate(`/content/${event.contentId}/schedule`);
        break;
      case 'publish':
        if (window.confirm('Are you sure you want to publish this content now?')) {
          // Call publish action
        }
        break;
      case 'approve':
        if (window.confirm('Are you sure you want to approve this content?')) {
          // Call approve action
        }
        break;
      case 'reject':
        // Open rejection dialog or navigate to rejection page
        navigate(`/content/${event.contentId}/reject`);
        break;
      case 'delete':
        if (window.confirm('Are you sure you want to delete this content? This action cannot be undone.')) {
          // Call delete action
        }
        break;
      default:
        break;
    }
  };
  
  // Handle filter changes
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    if (name === 'brand') {
      setBrandFilter(value);
    } else if (name === 'status') {
      setStatusFilter(value);
    }
    
    // Reload data with new filters
    loadCalendarData();
  };
  
  // Custom renderer for events in the calendar grid
  const renderCalendarEvent = (event, onClick, onAction) => {
    return (
      <CalendarEvent 
        event={event} 
        onClick={onClick}
        onActionClick={onAction}
      />
    );
  };
  
  // Helper function to get events for a specific date
  const getEventsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events[dateStr] || [];
  };
  
  // Loading state for initial load
  if (isLoading && isInitialLoad) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <MagicLoader variant="magical" size="medium" message="Loading calendar..." />
      </Box>
    );
  }
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Calendar Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        flexWrap: 'wrap',
        mb: 3,
        gap: 2 
      }}>
        {/* Left side - Title and navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={handlePrevious}>
            <ChevronLeftIcon />
          </IconButton>
          
          <IconButton onClick={handleNext}>
            <ChevronRightIcon />
          </IconButton>
          
          <Button
            size="small"
            onClick={handleToday}
            startIcon={<TodayIcon />}
            sx={{ ml: 1 }}
          >
            Today
          </Button>
          
          <Typography variant="h6" sx={{ ml: 2, fontWeight: 'medium' }}>
            {viewMode === 'month' && format(selectedDate, 'MMMM yyyy')}
            {viewMode === 'week' && `Week of ${format(startOfWeek(selectedDate), 'MMM d')} - ${format(endOfWeek(selectedDate), 'MMM d, yyyy')}`}
            {viewMode === 'day' && format(selectedDate, 'MMMM d, yyyy')}
          </Typography>
        </Box>
        
        {/* Right side - View options and create button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* View mode toggles */}
          <Box sx={{ 
            display: 'flex', 
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            borderRadius: 1,
            p: 0.5
          }}>
            <Tooltip title="Month View">
              <IconButton 
                color={viewMode === 'month' ? 'primary' : 'default'}
                onClick={() => handleViewModeChange('month')}
                size="small"
              >
                <MonthViewIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Week View">
              <IconButton 
                color={viewMode === 'week' ? 'primary' : 'default'}
                onClick={() => handleViewModeChange('week')}
                size="small"
              >
                <WeekViewIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Day View">
              <IconButton 
                color={viewMode === 'day' ? 'primary' : 'default'}
                onClick={() => handleViewModeChange('day')}
                size="small"
              >
                <DayViewIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={statusFilter}
                label="Status"
                onChange={handleFilterChange}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="pendingApproval">Pending Approval</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="published">Published</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          {canCreate && (
            <Button
              variant="contained"
              
              startIcon={<AddIcon />}
              onClick={() => navigate('/content/create')}
              magical={true}
            >
              Create Content
            </Button>
          )}
        </Box>
      </Box>
      
      {/* Loading state for content but not initial load */}
      {isLoading && !isInitialLoad && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <MagicLoader variant="standard" size="small" message="Loading calendar data..." />
        </Box>
      )}
      
      {/* Calendar Grid */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {viewMode === 'month' && (
          <CalendarGrid
            date={selectedDate}
            events={events}
            onDayClick={handleDayClick}
            onEventClick={handleEventClick}
            onEventAction={handleEventAction}
            renderEvent={renderCalendarEvent}
          />
        )}
        
        {viewMode === 'week' && (
          <CalendarGrid
            date={selectedDate}
            events={events}
            onDayClick={handleDayClick}
            onEventClick={handleEventClick}
            onEventAction={handleEventAction}
            renderEvent={renderCalendarEvent}
            cellHeight={180}
          />
        )}
        
        {viewMode === 'day' && (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </Typography>
            
            <Paper sx={{ p: 2 }}>
              {getEventsForDate(selectedDate).length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <ScheduleIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="textSecondary">
                    No content scheduled for this day
                  </Typography>
                  {canCreate && (
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => navigate('/content/create')}
                      sx={{ mt: 2 }}
                    >
                      Create Content
                    </Button>
                  )}
                </Box>
              ) : (
                <Box>
                  {getEventsForDate(selectedDate).map((event, index) => (
                    <CalendarEvent
                      key={index}
                      event={event}
                      onClick={() => handleEventClick(event)}
                      onActionClick={handleEventAction}
                    />
                  ))}
                </Box>
              )}
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ContentCalendar;